// ---------------------------------------------------------------------------
// Feature-gated sidebar links
// ---------------------------------------------------------------------------

/**
 * Conditionally inject feature-gated sidebar links.
 *
 * Called after the /api/me/ response is parsed, passing the `features` dict
 * from the user object. Links are only added to the DOM when the corresponding
 * feature flag is exactly `true` (strict equality, per Req 8.4):
 *   - `features.audit_visibility === true` → "Registro de actividad"
 *   - `features.error_collection === true` → "Recolección de errores"
 *
 * If the placeholder element doesn't exist, or `features` is absent/falsy,
 * or a flag is not exactly `true`, the corresponding placeholder stays empty —
 * the link is absent from the DOM (Req 8.2).
 *
 * When a feature is in `featuresBlocked` (parent-enabled but child-disabled)
 * AND the user is an administrador, a greyed-out disabled item with an
 * informational hover tooltip is injected instead of the active link.
 *
 * @param {Record<string, boolean> | null | undefined} features
 * @param {string | null | undefined} backendRole  The resolved backend role ('administrador' | 'vendedor' | null).
 *   Both `features` flags AND `backendRole === 'administrador'` must be true for a link to be injected.
 * @param {string[]} featuresBlocked  Features the parent enabled but this child branch disabled.
 */
function hydrateSidebarFeatures(features, backendRole, featuresBlocked = []) {
    // ── Reset placeholders before each hydration pass ─────────────────────────
    // Keeps placeholders in the DOM so library-switch re-hydration works.
    // replaceWith() would permanently remove the element ID; instead we clear
    // and repopulate so subsequent calls (on librería switch) always find the node.
    ['sidebar-registros-actividad-placeholder', 'sidebar-recoleccion-errores-placeholder'].forEach(function(id) {
        const el = document.getElementById(id);
        if (el) { el.innerHTML = ''; el.className = 'admin-only'; }
    });
    // Nothing to inject when features dict is absent or both flags are unset.
    // Strict equality checks — truthy is not enough (Req 8.4).
    if (!features && (!Array.isArray(featuresBlocked) || featuresBlocked.length === 0)) {
        return;
    }

    // ── Singleton blocked-feature tooltip ────────────────────────────────────
    // Created once per page and reused for both feature placeholders.
    // Matches the editoriales ISBN popover pattern exactly:
    //   - fixed, z-50, JS-positioned on mouseenter
    //   - show/hide via classList toggle of 'hidden' + 100ms setTimeout
    // Content is a STATIC string literal set via textContent — never innerHTML
    // with server data (XSS prevention: D.6 / Req B.5).
    const _getBlockedTooltip = (() => {
        let el = null;
        let hideTimer = null;
        return {
            get() {
                if (!el) {
                    el = document.createElement('div');
                    el.className = 'fixed z-50 hidden max-w-sm rounded-lg border-2 border-gray-300 bg-white p-3 shadow-2xl dark:border-gray-500 dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-200';
                    el.setAttribute('role', 'tooltip');
                    // Static string — never innerHTML with any server-returned value
                    el.textContent = 'Contacta con el administrador de tu cuenta para habilitar esta funcionalidad';
                    document.body.appendChild(el);
                }
                return el;
            },
            show(anchorEl) {
                clearTimeout(hideTimer);
                const tip = this.get();
                const rect = anchorEl.getBoundingClientRect();
                const tipW = 260;
                let left = rect.right + 8;
                if (left + tipW > window.innerWidth) left = rect.left - tipW - 8;
                tip.style.left = `${Math.max(8, left)}px`;
                tip.style.top = `${rect.top}px`;
                tip.style.width = `${tipW}px`;
                tip.classList.remove('hidden');
            },
            hide() {
                clearTimeout(hideTimer);
                const tip = this.get();
                hideTimer = setTimeout(() => tip.classList.add('hidden'), 100);
            },
        };
    })();

    // ── audit_visibility: inject "Registro de actividad" link ────────────────
    if (features && features.audit_visibility === true && !featuresBlocked.includes('audit_visibility') && backendRole === 'administrador') {
        const placeholder = document.getElementById('sidebar-registros-actividad-placeholder');
        if (placeholder) {
            const isActive = (document.body.dataset.pageSlug || '') === 'registros_actividad';

            // Build the <li> DOM node entirely via safe DOM APIs — never innerHTML with
            // any server-returned string value (XSS prevention, Req 8.2).
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.href = '/crud/registros_actividad/';
            a.className = [
                'text-base',
                'text-gray-900',
                'rounded-lg',
                'flex',
                'items-center',
                'p-2',
                'group',
                'hover:bg-gray-100',
                'transition',
                'duration-75',
                'pl-11',
                'dark:text-gray-200',
                'dark:hover:bg-gray-700',
                isActive ? 'bg-gray-100 dark:bg-gray-700' : '',
            ].filter(Boolean).join(' ');

            // textContent only — never innerHTML with user/server-controlled data
            a.textContent = 'Registro de actividad';

            placeholder.appendChild(a);
        }
    } else if (Array.isArray(featuresBlocked) && featuresBlocked.includes('audit_visibility') && backendRole === 'administrador') {
        // Parent-enabled but child-blocked: show a disabled item with informational tooltip
        const placeholder = document.getElementById('sidebar-registros-actividad-placeholder');
        if (placeholder) {
            const li = document.createElement('li');
            li.className = 'admin-only';
            const span = document.createElement('span');
            // Same visual class as the active link but with disabled state (opacity + no-pointer)
            span.className = [
                'text-base',
                'text-gray-900',
                'rounded-lg',
                'flex',
                'items-center',
                'p-2',
                'group',
                'transition',
                'duration-75',
                'pl-11',
                'dark:text-gray-200',
                'opacity-50',
                'cursor-not-allowed',
            ].join(' ');
            // Static text content — never innerHTML with server data (XSS: D.6)
            span.textContent = 'Registro de actividad';
            placeholder.appendChild(span);
            // Tooltip: mouseenter/mouseleave — matches editoriales ISBN popover mechanism
            placeholder.addEventListener('mouseenter', () => _getBlockedTooltip.show(placeholder));
            placeholder.addEventListener('mouseleave', () => _getBlockedTooltip.hide());
        }
    }

    // ── error_collection: inject "Recolección de errores" link ───────────────
    if (features && features.error_collection === true && !featuresBlocked.includes('error_collection') && backendRole === 'administrador') {
        const placeholder2 = document.getElementById('sidebar-recoleccion-errores-placeholder');
        if (placeholder2) {
            const isActive2 = (document.body.dataset.pageSlug || '') === 'recoleccion_errores';

            // Build the <li> DOM node entirely via safe DOM APIs — never innerHTML with
            // any server-returned string value (XSS prevention, Req 8.2).
            const li2 = document.createElement('li');
            const a2 = document.createElement('a');
            a2.href = '/crud/recoleccion_errores/';
            a2.className = [
                'text-base',
                'text-gray-900',
                'rounded-lg',
                'flex',
                'items-center',
                'p-2',
                'group',
                'hover:bg-gray-100',
                'transition',
                'duration-75',
                'pl-11',
                'dark:text-gray-200',
                'dark:hover:bg-gray-700',
                isActive2 ? 'bg-gray-100 dark:bg-gray-700' : '',
            ].filter(Boolean).join(' ');

            // textContent only — never innerHTML with user/server-controlled data
            a2.textContent = 'Recolección de errores';

            placeholder2.appendChild(a2);
        }
    } else if (Array.isArray(featuresBlocked) && featuresBlocked.includes('error_collection') && backendRole === 'administrador') {
        // Parent-enabled but child-blocked: show a disabled item with informational tooltip
        const placeholder2 = document.getElementById('sidebar-recoleccion-errores-placeholder');
        if (placeholder2) {
            const li2 = document.createElement('li');
            li2.className = 'admin-only';
            const span2 = document.createElement('span');
            span2.className = [
                'text-base',
                'text-gray-900',
                'rounded-lg',
                'flex',
                'items-center',
                'p-2',
                'group',
                'transition',
                'duration-75',
                'pl-11',
                'dark:text-gray-200',
                'opacity-50',
                'cursor-not-allowed',
            ].join(' ');
            // Static text content — never innerHTML with server data (XSS: D.6)
            span2.textContent = 'Recolección de errores';
            placeholder2.appendChild(span2);
            placeholder2.addEventListener('mouseenter', () => _getBlockedTooltip.show(placeholder2));
            placeholder2.addEventListener('mouseleave', () => _getBlockedTooltip.hide());
        }
    }
}

// Expose so user-role.js can call it after parsing the /api/me/ response
window.hydrateSidebarFeatures = hydrateSidebarFeatures;

// Consume any pending features that user-role.js stored before sidebar.js loaded
// (script-order safety — user-role.js may resolve /api/me/ before this script runs).
if (typeof window._pendingSidebarFeatures !== 'undefined') {
  hydrateSidebarFeatures(
    window._pendingSidebarFeatures,
    window._pendingSidebarBackendRole,
    window._pendingSidebarFeaturesBlocked ?? []
  );
  window._pendingSidebarFeatures = undefined;
  window._pendingSidebarBackendRole = undefined;
  window._pendingSidebarFeaturesBlocked = undefined;
}

// ---------------------------------------------------------------------------
// Sidebar UI (collapse, mobile toggle, favourites)
// ---------------------------------------------------------------------------

const sidebar = document.getElementById('sidebar');

if (sidebar) {
    // -------------------------------------------------------------------------
    // Desktop sidebar collapse / expand
    // -------------------------------------------------------------------------
    const SIDEBAR_COLLAPSED_KEY = 'sidebarDesktopCollapsed';

    /**
     * Apply the collapsed/expanded visual state without toggling — lets us
     * call this on page load to restore the saved preference.
     *
     * @param {boolean} collapsed
     */
    const applySidebarDesktopState = (collapsed) => {
        sidebar.setAttribute('data-collapsed', collapsed ? 'true' : 'false');
        // Mirror state on <html> so CSS can reference it for the search form position.
        document.documentElement.setAttribute('data-sidebar-collapsed', collapsed ? 'true' : 'false');

        const toggleSidebarDesktopBtn = document.getElementById('toggleSidebarDesktop');
        const toggleSidebarDesktopCollapse = document.getElementById('toggleSidebarDesktopCollapse');
        const toggleSidebarDesktopExpand = document.getElementById('toggleSidebarDesktopExpand');

        if (toggleSidebarDesktopBtn) {
            toggleSidebarDesktopBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
            toggleSidebarDesktopBtn.setAttribute(
                'aria-label',
                collapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'
            );
            toggleSidebarDesktopBtn.setAttribute(
                'title',
                collapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'
            );
        }

        if (toggleSidebarDesktopCollapse) {
            toggleSidebarDesktopCollapse.classList.toggle('hidden', collapsed);
        }
        if (toggleSidebarDesktopExpand) {
            toggleSidebarDesktopExpand.classList.toggle('hidden', !collapsed);
        }
    };

    const toggleSidebarDesktop = () => {
        const isCollapsed = sidebar.getAttribute('data-collapsed') === 'true';
        applySidebarDesktopState(!isCollapsed);
        try {
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, !isCollapsed ? '1' : '0');
        } catch (_) {
            // localStorage unavailable — proceed without persistence
        }
    };

    // app.bundle.js runs at the bottom of <body> — the full DOM is already
    // parsed, so getElementById works synchronously here. No DOMContentLoaded
    // wrapper needed (and it would never fire at this point anyway).
    const toggleSidebarDesktopBtn = document.getElementById('toggleSidebarDesktop');
    if (toggleSidebarDesktopBtn) {
        toggleSidebarDesktopBtn.addEventListener('click', toggleSidebarDesktop);
    }

    // Track the width of the navbar left cluster (logo + toggle button) so CSS
    // can position the search form right of it when the sidebar is collapsed.
    const navbarLeftCluster = document.getElementById('navbar-left-cluster');
    if (navbarLeftCluster && window.ResizeObserver) {
        const updateNavbarLeftW = () => {
            document.documentElement.style.setProperty(
                '--navbar-left-w',
                navbarLeftCluster.offsetWidth + 'px'
            );
        };
        new ResizeObserver(updateNavbarLeftW).observe(navbarLeftCluster);
        updateNavbarLeftW();
    }

    // Track the logo link width so CSS can calculate the toggle button's
    // midpoint margin: (sidebar-w - navbar-logo-w) / 2 - button-half-width.
    const navbarLogoLink = document.getElementById('navbar-logo-link');
    if (navbarLogoLink && window.ResizeObserver) {
        const updateNavbarLogoW = () => {
            document.documentElement.style.setProperty(
                '--navbar-logo-w',
                navbarLogoLink.offsetWidth + 'px'
            );
        };
        new ResizeObserver(updateNavbarLogoW).observe(navbarLogoLink);
        updateNavbarLogoW();
    }

    // Restore saved preference (only on large screens where the desktop
    // sidebar is actually visible).
    if (window.innerWidth >= 1024) {
        try {
            const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
            if (saved === '1') {
                applySidebarDesktopState(true);
            } else {
                // Explicitly mark as expanded so CSS [data-sidebar-collapsed="false"] rules work
                document.documentElement.setAttribute('data-sidebar-collapsed', 'false');
            }
        } catch (_) {
            document.documentElement.setAttribute('data-sidebar-collapsed', 'false');
        }
    } else {
        // On mobile the desktop collapse concept doesn't apply — mark as expanded
        document.documentElement.setAttribute('data-sidebar-collapsed', 'false');
    }

    // -------------------------------------------------------------------------
    // Keep --sidebar-w in sync with the sidebar's actual rendered width so
    // the main content margin tracks it dynamically (see style.css .sidebar-offset).
    // -------------------------------------------------------------------------
    if (window.ResizeObserver) {
        const updateSidebarWidth = () => {
            document.documentElement.style.setProperty(
                '--sidebar-w',
                sidebar.offsetWidth + 'px'
            );
        };
        new ResizeObserver(updateSidebarWidth).observe(sidebar);
        updateSidebarWidth(); // set initial value immediately
    }
    const FAVORITES_STORAGE_KEY = 'sidebarFavoriteLinks';
    const FAVORITES_LIMIT = 3;
    const FAVORITES_LIMIT_MODAL_ID = 'sidebar-favorites-limit-modal';
    const FAVORITES_LIMIT_MODAL_TITLE_ID = 'sidebar-favorites-limit-modal-title';
    const FAVORITES_LIMIT_MESSAGE = 'Hasta 3 accesos directos desde los submenus.';

    // Feature flag — set by the Plataforma config section in configuracion.html.
    // Default: enabled (true). Stored as '0' when disabled.
    const FAVORITES_ENABLED_KEY = 'sidebarFavoritesEnabled';
    const isFavoritesEnabled = () => {
        try {
            return localStorage.getItem(FAVORITES_ENABLED_KEY) !== '0';
        } catch (_) {
            return true;
        }
    };
    // Expose so configuracion.html can call it after toggling.
    // This is an early stub that only persists to localStorage.
    // On pages with the full sidebar (favorites elements present), this gets
    // overwritten with a richer implementation that also applies the UI state.
    window.setSidebarFavoritesEnabled = (enabled) => {
        try {
            localStorage.setItem(FAVORITES_ENABLED_KEY, enabled ? '1' : '0');
        } catch (_) {}
        // Note: applyFavoritesEnabledState is defined later inside the favorites
        // block; this stub can't call it safely. The overwrite at line ~693 handles
        // pages where the full sidebar exists.
    };
    window.getSidebarFavoritesEnabled = isFavoritesEnabled;

    const toggleSidebarMobile = (sidebar, sidebarBackdrop, toggleSidebarMobileHamburger, toggleSidebarMobileClose) => {
        sidebar.classList.toggle('hidden');
        sidebarBackdrop.classList.toggle('hidden');
        toggleSidebarMobileHamburger.classList.toggle('hidden');
        toggleSidebarMobileClose.classList.toggle('hidden');
    };
    
    const toggleSidebarMobileEl = document.getElementById('toggleSidebarMobile');
    const sidebarBackdrop = document.getElementById('sidebarBackdrop');
    const toggleSidebarMobileHamburger = document.getElementById('toggleSidebarMobileHamburger');
    const toggleSidebarMobileClose = document.getElementById('toggleSidebarMobileClose');
    const toggleSidebarMobileSearch = document.getElementById('toggleSidebarMobileSearch');
    
    if (
        toggleSidebarMobileEl &&
        sidebarBackdrop &&
        toggleSidebarMobileHamburger &&
        toggleSidebarMobileClose
    ) {
        // toggleSidebarMobileSearch was removed from the navbar — guard it.
        if (toggleSidebarMobileSearch) {
            toggleSidebarMobileSearch.addEventListener('click', () => {
                toggleSidebarMobile(sidebar, sidebarBackdrop, toggleSidebarMobileHamburger, toggleSidebarMobileClose);
            });
        }

        toggleSidebarMobileEl.addEventListener('click', () => {
            toggleSidebarMobile(sidebar, sidebarBackdrop, toggleSidebarMobileHamburger, toggleSidebarMobileClose);
        });

        sidebarBackdrop.addEventListener('click', () => {
            toggleSidebarMobile(sidebar, sidebarBackdrop, toggleSidebarMobileHamburger, toggleSidebarMobileClose);
        });

        // When the viewport widens back to desktop (e.g. closing docked DevTools),
        // dismiss the mobile backdrop and restore the hamburger/close icon state so
        // the desktop sidebar renders correctly without a full reload.
        let _mobileResizeDebounce = null;
        window.addEventListener('resize', () => {
            clearTimeout(_mobileResizeDebounce);
            _mobileResizeDebounce = setTimeout(() => {
                if (window.innerWidth >= 1024) {
                    sidebarBackdrop.classList.add('hidden');
                    // Restore hamburger icon: visible, close icon: hidden
                    toggleSidebarMobileHamburger.classList.remove('hidden');
                    toggleSidebarMobileClose.classList.add('hidden');
                }
            }, 100);
        }, { passive: true });
    }

    sidebar.querySelectorAll('[data-collapse-toggle]').forEach((toggleButton) => {
        const targetId = toggleButton.getAttribute('data-collapse-toggle');
        const target = targetId && document.getElementById(targetId);
        const chevron = toggleButton.querySelector('svg[sidebar-toggle-item]:last-of-type');

        if (!target || !chevron) {
            return;
        }

        chevron.classList.add('transition-transform', 'duration-200');

        const syncChevronDirection = () => {
            chevron.classList.toggle('rotate-180', !target.classList.contains('hidden'));
        };

        syncChevronDirection();

        new MutationObserver(syncChevronDirection).observe(target, {
            attributes: true,
            attributeFilter: ['class'],
        });
    });

    const menuList = sidebar.querySelector('ul.flex-grow');
    const favoritesSection = document.getElementById('sidebar-favorites-section');
    const favoritesList = document.getElementById('sidebar-favorites-list');
    const favoritesEmptyState = document.getElementById('sidebar-favorites-empty');

    if (menuList && favoritesSection && favoritesList && favoritesEmptyState) {
    const normalizePath = (value) => {
        try {
            const pathname = new URL(value, window.location.origin).pathname;
            return pathname !== '/' && pathname.endsWith('/')
                ? pathname.slice(0, -1)
                : pathname;
        } catch (error) {
            return value;
        }
    };

    const loadFavorites = () => {
        try {
            const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
            const parsed = JSON.parse(stored);

            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.warn('No se pudo leer la configuración de favoritos del menú.', error);
            return [];
        }
    };

    const saveFavorites = (favorites) => {
        try {
            localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
        } catch (error) {
            console.warn('No se pudo guardar la configuración de favoritos del menú.', error);
        }
    };

    let favoritesLimitModal;

    const getFavoritesLimitModal = () => {
        if (favoritesLimitModal) {
            return favoritesLimitModal;
        }

        const modalElement = document.createElement('div');
        modalElement.id = FAVORITES_LIMIT_MODAL_ID;
        modalElement.setAttribute('role', 'dialog');
        modalElement.setAttribute('aria-modal', 'true');
        modalElement.setAttribute('aria-hidden', 'true');
        modalElement.setAttribute('aria-labelledby', FAVORITES_LIMIT_MODAL_TITLE_ID);
        modalElement.className = 'fixed inset-0 z-50 hidden items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm';

        const card = document.createElement('div');
        card.className = [
            'relative w-full max-w-sm rounded-2xl shadow-2xl',
            'bg-white dark:bg-gray-800',
            'border border-gray-200 dark:border-gray-600',
        ].join(' ');

        // ── Close button (×) ────────────────────────────────────────────
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = [
            'absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full',
            'text-gray-400 dark:text-gray-500',
            'hover:bg-gray-100 dark:hover:bg-gray-700',
            'hover:text-gray-600 dark:hover:text-gray-300',
            'transition focus:outline-none focus:ring-2 focus:ring-primary-500',
        ].join(' ');
        closeBtn.setAttribute('data-favorites-limit-close', '');
        closeBtn.setAttribute('aria-label', 'Cerrar aviso de favoritos');
        const closeSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        closeSvg.setAttribute('class', 'h-4 w-4');
        closeSvg.setAttribute('viewBox', '0 0 14 14');
        closeSvg.setAttribute('fill', 'none');
        closeSvg.setAttribute('aria-hidden', 'true');
        const closePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        closePath.setAttribute('d', 'M1 1l12 12M13 1L1 13');
        closePath.setAttribute('stroke', 'currentColor');
        closePath.setAttribute('stroke-linecap', 'round');
        closePath.setAttribute('stroke-linejoin', 'round');
        closePath.setAttribute('stroke-width', '1.5');
        closeSvg.appendChild(closePath);
        closeBtn.appendChild(closeSvg);
        card.appendChild(closeBtn);

        // ── Body ────────────────────────────────────────────────────────
        const body = document.createElement('div');
        body.className = 'px-6 pt-6 pb-6 flex flex-col items-center text-center gap-4';

        // Icon badge
        const iconWrap = document.createElement('div');
        iconWrap.className = [
            'flex items-center justify-center w-14 h-14 rounded-full',
            'bg-amber-100 dark:bg-amber-500/15',
            'text-amber-500 dark:text-amber-400',
        ].join(' ');
        const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        iconSvg.setAttribute('class', 'h-7 w-7');
        iconSvg.setAttribute('viewBox', '0 0 20 20');
        iconSvg.setAttribute('fill', 'currentColor');
        iconSvg.setAttribute('aria-hidden', 'true');
        const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        iconPath.setAttribute('d', 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z');
        iconSvg.appendChild(iconPath);
        iconWrap.appendChild(iconSvg);
        body.appendChild(iconWrap);

        // Text block
        const textBlock = document.createElement('div');
        const title = document.createElement('h3');
        title.id = FAVORITES_LIMIT_MODAL_TITLE_ID;
        title.className = 'text-base font-semibold text-gray-900 dark:text-white';
        title.textContent = 'Límite de favoritos';
        const msg = document.createElement('p');
        msg.className = 'mt-1 text-sm text-gray-500 dark:text-gray-400 leading-relaxed';
        msg.textContent = FAVORITES_LIMIT_MESSAGE;
        textBlock.appendChild(title);
        textBlock.appendChild(msg);
        body.appendChild(textBlock);

        // OK button
        const okBtn = document.createElement('button');
        okBtn.type = 'button';
        okBtn.className = [
            'w-full inline-flex items-center justify-center rounded-lg px-4 py-2.5',
            'text-sm font-semibold text-white',
            'bg-amber-500 hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-400',
            'focus:outline-none focus:ring-4 focus:ring-amber-300 dark:focus:ring-amber-500/40',
            'transition',
        ].join(' ');
        okBtn.setAttribute('data-favorites-limit-close', '');
        okBtn.textContent = 'Entendido';
        body.appendChild(okBtn);

        card.appendChild(body);
        modalElement.appendChild(card);

        const closeModal = () => {
            modalElement.classList.add('hidden');
            modalElement.classList.remove('flex');
            modalElement.setAttribute('aria-hidden', 'true');
        };

        const openModal = () => {
            modalElement.classList.remove('hidden');
            modalElement.classList.add('flex');
            modalElement.setAttribute('aria-hidden', 'false');
            modalElement.querySelector('[data-favorites-limit-close]')?.focus();
        };

        modalElement.addEventListener('click', (event) => {
            if (event.target === modalElement) {
                closeModal();
            }
        });

        modalElement.querySelectorAll('[data-favorites-limit-close]').forEach((button) => {
            button.addEventListener('click', closeModal);
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !modalElement.classList.contains('hidden')) {
                closeModal();
            }
        });

        document.body.appendChild(modalElement);

        favoritesLimitModal = {
            close: closeModal,
            open: openModal,
        };

        return favoritesLimitModal;
    };

    const getTopLevelGroup = (link) => {
        return Array.from(menuList.children).find((item) => item.contains(link) && item !== favoritesSection);
    };

    const getTopLevelGroupLabel = (topLevelGroup) => {
        if (!topLevelGroup) {
            return '';
        }

        const directAction = Array.from(topLevelGroup.children).find((child) => child.matches('a, button'));
        return directAction?.querySelector('[sidebar-toggle-item]')?.textContent?.trim() || '';
    };

    const getFavoriteCandidates = () => {
        const uniqueCandidates = new Map();

        Array.from(menuList.querySelectorAll(':scope > li ul a'))
            .filter((link) => !favoritesSection.contains(link))
            .forEach((link) => {
                const href = link.getAttribute('href') || link.href;
                const key = normalizePath(href);

                if (!key || uniqueCandidates.has(key)) {
                    return;
                }

                const topLevelGroup = getTopLevelGroup(link);
                const groupLabel = getTopLevelGroupLabel(topLevelGroup);

                uniqueCandidates.set(key, {
                    key,
                    href,
                    label: link.textContent.trim(),
                    link,
                    groupLabel,
                });
            });

        return uniqueCandidates;
    };

    const favoriteCandidates = getFavoriteCandidates();
    const favoriteButtons = new Map();
    const storedFavorites = loadFavorites();
    let favorites = storedFavorites.filter((key) => favoriteCandidates.has(key)).slice(0, FAVORITES_LIMIT);

    if (favorites.length !== storedFavorites.length) {
        saveFavorites(favorites);
    }

    const updateFavoriteButtonState = (button, isFavorite) => {
        if (!button) return;

        button.setAttribute('aria-pressed', isFavorite ? 'true' : 'false');
        button.setAttribute('title', isFavorite ? 'Quitar de favoritos' : 'Añadir a favoritos');
        button.classList.toggle('text-amber-500', isFavorite);
        button.classList.toggle('dark:text-amber-400', isFavorite);
        button.classList.toggle('text-gray-400', !isFavorite);
        button.classList.toggle('dark:text-gray-500', !isFavorite);
        button.classList.toggle('hover:text-amber-500', !isFavorite);
    };

    const renderFavorites = () => {
        const currentPath = normalizePath(window.location.pathname);
        favoritesList.innerHTML = '';

        // Hide the entire Favoritos section when there are no saved favorites.
        // Show it (and keep its dropdown open) as soon as one is added.
        const hasFavorites = favorites.length > 0;
        favoritesSection.classList.toggle('hidden', !hasFavorites);

        // Ensure the dropdown is open whenever the section is visible.
        if (hasFavorites) {
            const dropdown = document.getElementById('dropdown-favoritos');
            if (dropdown) {
                dropdown.classList.remove('hidden');
            }
        }

        // The empty-state element is kept in the DOM for backwards compat
        // but is always hidden now — section visibility replaces it.
        favoritesEmptyState.classList.add('hidden');

        favorites.forEach((key) => {
            const favorite = favoriteCandidates.get(key);

            if (!favorite) {
                return;
            }

            const item = document.createElement('li');
            const link = document.createElement('a');
            const isActive = currentPath === key;

            link.href = favorite.href;
            link.className = [
                'flex',
                'items-center',
                'justify-between',
                'gap-2',
                'rounded-lg',
                'px-3',
                'py-2',
                'text-sm',
                'transition',
                isActive
                    ? 'bg-white/80 text-amber-900 shadow-sm dark:bg-gray-700 dark:text-white'
                    : 'text-amber-900/90 hover:bg-white/70 dark:text-amber-100 dark:hover:bg-gray-700/70',
            ].join(' ');

            link.innerHTML = `
                <span class="truncate font-medium">${favorite.label}</span>
                <span class="shrink-0 text-[11px] uppercase tracking-wide text-amber-700/80 dark:text-amber-200/80">${favorite.groupLabel}</span>
            `;

            item.appendChild(link);
            favoritesList.appendChild(item);
        });
    };

    const toggleFavorite = (favorite) => {
        const isFavorite = favorites.includes(favorite.key);

        if (isFavorite) {
            favorites = favorites.filter((key) => key !== favorite.key);
        } else {
            if (favorites.length >= FAVORITES_LIMIT) {
                getFavoritesLimitModal().open();
                return;
            }

            favorites = [...favorites, favorite.key];
        }

        saveFavorites(favorites);
        renderFavorites();

        favoriteCandidates.forEach((item) => {
            updateFavoriteButtonState(favoriteButtons.get(item.key), favorites.includes(item.key));
        });
    };

    const createFavoriteButton = (favorite) => {
        const button = document.createElement('button');
        const isFavorite = favorites.includes(favorite.key);

        button.type = 'button';
        button.className = 'favorite-toggle absolute top-1/2 -translate-y-1/2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500';
        button.innerHTML = '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>';

        updateFavoriteButtonState(button, isFavorite);

        button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleFavorite(favorite);
        });

        favoriteButtons.set(favorite.key, button);
        return button;
    };

    favoriteCandidates.forEach((favorite) => {
        favorite.link.appendChild(createFavoriteButton(favorite));
    });

    renderFavorites();

    // Apply enabled/disabled state — show/hide star buttons, padding, and the section.
    const applyFavoritesEnabledState = (enabled) => {
        // Drive the CSS pr-8 padding via a single data attribute on #sidebar —
        // the stylesheet rule only applies when data-favorites-enabled="true".
        sidebar.setAttribute('data-favorites-enabled', enabled ? 'true' : 'false');

        // Show/hide each star button.
        favoriteButtons.forEach((btn) => {
            btn.classList.toggle('hidden', !enabled);
        });

        // If disabling, hide the favorites section unconditionally.
        if (!enabled) {
            favoritesSection.classList.add('hidden');
        } else {
            // Re-render to show/hide section based on whether favorites exist.
            renderFavorites();
        }
    };

    // Apply on page load.
    applyFavoritesEnabledState(isFavoritesEnabled());

    // Expose for configuracion.html — overwrite the early stub with the real impl
    // that has closure access to applyFavoritesEnabledState and favoriteButtons.
    window.setSidebarFavoritesEnabled = (enabled) => {
        try {
            localStorage.setItem(FAVORITES_ENABLED_KEY, enabled ? '1' : '0');
        } catch (_) {}
        applyFavoritesEnabledState(enabled);
    };

    } // end favorites section

    // -------------------------------------------------------------------------
    // Section-level idle-collapse
    // -------------------------------------------------------------------------
    // Only runs when the sidebar itself is *expanded* (not icon-collapsed).
    // Does not affect mobile.
    //
    // Behaviour:
    //   • On page load: the active section stays open; all others start closed.
    //   • On sidebar mouseleave: after IDLE_DELAY_MS, close all non-active sections.
    //   • Clicking a section button permanently toggles it (existing behaviour kept).
    // -------------------------------------------------------------------------
    (function initSectionIdleCollapse() {
        // Only applies on large screens (same breakpoint as the desktop sidebar).
        if (window.innerWidth < 1024) return;

        const IDLE_DELAY_MS       = 4000;  // ms after sidebar mouseleave → collapse

        // IDs of the top-level section dropdowns we manage (label groups).
        // These are the <ul> elements whose open/close we animate.
        const SECTION_DROPDOWN_IDS = [
            'dropdown-favoritos',
            'dropdown-pages-pedidos',
            'dropdown-pages-inventario',
            'dropdown-pages-notificaciones',
            'dropdown-pages-novedades',
            'dropdown-pages-postventa',
            'dropdown-pages-contabilidad',
            'dropdown-estadisticas',
            'dropdown-sinli',
            'dropdown-pages2',
            'dropdown-playground',
            'dropdown-plan',
        ];

        // IDs of nested submenus (inside a top-level section).
        // These get click-to-toggle but NOT idle-collapse (parent collapse handles that).
        const NESTED_DROPDOWN_IDS = [
            'dropdown-pages-pedidos-proveedores',
            'dropdown-introduccion-mercancia',
            'dropdown-regular-stock',
            'dropdown-devoluciones',
        ];

        // Map each dropdown <ul> → its parent <li> (the section container).
        /** @type {Map<HTMLElement, HTMLElement>} */
        const sectionMap = new Map(); // ul → li

        SECTION_DROPDOWN_IDS.forEach((id) => {
            const ul = document.getElementById(id);
            if (!ul) return;
            const li = ul.closest('li');
            if (!li) return;
            sectionMap.set(ul, li);
        });

        if (sectionMap.size === 0) return;

        // -----------------------------------------------------------------
        // Determine which section is "active" (contains the current page link).
        // Hugo injects bg-gray-100 on the active <a>. We check for that class
        // only — dark: prefixed classes contain a colon and cannot be used
        // as a plain querySelector selector string.
        // -----------------------------------------------------------------
        const getActiveSectionUl = () => {
            for (const ul of sectionMap.keys()) {
                if (ul.querySelector('a.bg-gray-100')) {
                    return ul;
                }
            }
            return null;
        };

        const activeSectionUl = getActiveSectionUl();

        // -----------------------------------------------------------------
        // Open / close helpers — animate via CSS max-height class.
        // We add .sidebar-section-dropdown to the <ul> on init, then toggle
        // .sidebar-section-open to drive the CSS transition.
        // -----------------------------------------------------------------
        const openSection = (ul) => {
            ul.classList.add('sidebar-section-dropdown', 'sidebar-section-open');
            // Ensure Flowbite's hidden class is removed so the ul is in the flow.
            ul.classList.remove('hidden');
            // Keep chevron in sync — fire a synthetic class mutation.
            ul.dispatchEvent(new Event('sidebar:open', { bubbles: false }));
        };

        const closeSection = (ul) => {
            // Collapse all nested submenus inside this section first so they
            // start closed next time the parent re-opens.
            ul.querySelectorAll('.sidebar-section-dropdown').forEach((nested) => {
                nested.classList.remove('sidebar-section-open');
                nested.classList.add('hidden');
            });

            ul.classList.add('sidebar-section-dropdown');
            ul.classList.remove('sidebar-section-open');
            // After the transition, add Flowbite's hidden so assistive tech
            // treats it as collapsed (matches original behaviour).
            const onEnd = () => {
                // Only actually hide if still closed (user may have re-opened).
                if (!ul.classList.contains('sidebar-section-open')) {
                    ul.classList.add('hidden');
                }
                ul.removeEventListener('transitionend', onEnd);
            };
            ul.addEventListener('transitionend', onEnd);
            ul.dispatchEvent(new Event('sidebar:close', { bubbles: false }));
        };

        // -----------------------------------------------------------------
        // Bootstrap: apply the CSS class to all tracked dropdowns and set
        // their initial open/closed state.
        // Suppress animation on first paint by adding the open class before
        // the section is inserted into the CSS transition flow.
        // -----------------------------------------------------------------
        sectionMap.forEach((li, ul) => {
            if (ul === activeSectionUl) {
                // Active section: visible, open, no transition on load.
                ul.classList.remove('hidden');
                // Add both classes together so the transition never fires
                // (max-height starts at 600px immediately, no animation).
                ul.classList.add('sidebar-section-dropdown', 'sidebar-section-open');
            } else {
                // Non-active: ensure hidden, no open class.
                ul.classList.remove('sidebar-section-open');
                ul.classList.add('sidebar-section-dropdown', 'hidden');
            }
        });

        // -----------------------------------------------------------------
        // Idle timer — starts when the cursor leaves the sidebar.
        // -----------------------------------------------------------------
        let idleTimer = null;

        const startIdleTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                sectionMap.forEach((li, ul) => {
                    if (ul !== activeSectionUl) {
                        closeSection(ul);
                    }
                });
            }, IDLE_DELAY_MS);
        };

        const cancelIdleTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = null;
        };

        sidebar.addEventListener('mouseleave', startIdleTimer);
        sidebar.addEventListener('mouseenter', cancelIdleTimer);

        // -----------------------------------------------------------------
        // Collapse-others-on-dwell — per section <li>.
        //
        // collapseOthersTimer (on whole <li> mouseenter/leave):
        //    After COLLAPSE_OTHERS_DELAY_MS of resting anywhere inside a
        //    section, collapse all other open non-active sections.
        //    This gives the "graceful tidy-up while you browse" feel:
        //    if you dwell in Novedades for ~4s, Inventario folds away.
        // -----------------------------------------------------------------
        const COLLAPSE_OTHERS_DELAY_MS = 4250;

        sectionMap.forEach((li, ul) => {
            let collapseOthersTimer = null;

            // ── Collapse-others on dwell (whole <li>) ──
            li.addEventListener('mouseenter', () => {
                cancelIdleTimer();

                // After dwelling here, collapse all other open non-active sections.
                collapseOthersTimer = setTimeout(() => {
                    sectionMap.forEach((otherLi, otherUl) => {
                        if (otherUl !== ul && otherUl !== activeSectionUl) {
                            closeSection(otherUl);
                        }
                    });
                }, COLLAPSE_OTHERS_DELAY_MS);
            });

            li.addEventListener('mouseleave', () => {
                clearTimeout(collapseOthersTimer);
                collapseOthersTimer = null;
            });

            // ── Click = permanent toggle ──
            const btn = li.querySelector(':scope > button[data-collapse-toggle]');
            if (btn) {
                btn.addEventListener('click', () => {
                    setTimeout(() => {
                        if (ul.classList.contains('hidden')) {
                            ul.classList.remove('sidebar-section-open');
                        } else {
                            openSection(ul);
                        }
                    }, 0);
                });
            }
        });

        // -----------------------------------------------------------------
        // Nested submenus — click-to-open (parent idle-collapse handles close).
        // -----------------------------------------------------------------
        NESTED_DROPDOWN_IDS.forEach((id) => {
            const ul = document.getElementById(id);
            if (!ul) return;
            const li = ul.closest('li');
            if (!li) return;

            // Init: add the CSS class for transitions.
            ul.classList.add('sidebar-section-dropdown');
            if (!ul.classList.contains('hidden')) {
                ul.classList.add('sidebar-section-open');
            }

            // Click = permanent toggle.
            const btn = li.querySelector(':scope > button[data-collapse-toggle]');
            if (btn) {
                btn.addEventListener('click', () => {
                    setTimeout(() => {
                        if (ul.classList.contains('hidden')) {
                            ul.classList.remove('sidebar-section-open');
                        } else {
                            openSection(ul);
                        }
                    }, 0);
                });
            }
        });

        // Re-evaluate on window resize in case user resizes below 1024px.
        // Backdrop cleanup on desktop resize is handled by the mobile toggle
        // block's resize listener (registered inside the mobile-toggle guard).
        window.addEventListener('resize', () => {
            if (window.innerWidth < 1024) {
                cancelIdleTimer();
            }
        }, { passive: true });

    }()); // end initSectionIdleCollapse
}
